import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getReports } from "../services/reportsService";
import { isSupabaseConfigured } from "../supabase/client";
import { FALLBACK_REPORTS } from "../data/fallbackReports";
import { fyLabel, fyShort } from "../utils/fiscal";
import "./Blog.css";

const MotionLink = motion(Link);

// Report image with a graceful navy placeholder when there's no image
// (or it fails to load), so a card never shows a broken-image icon.
const ReportMedia = ({ image, quarter, className }) => {
  const [failed, setFailed] = useState(!image);
  if (failed) {
    return (
      <div className={`report-media-placeholder ${className || ""}`}>
        <span>{quarter}</span>
      </div>
    );
  }
  return (
    <img
      src={image}
      alt=""
      className={className}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const Blog = () => {
  const [reports, setReports] = useState(FALLBACK_REPORTS);

  // Once Supabase is configured & seeded, load reports live from Supabase.
  useEffect(() => {
    if (!isSupabaseConfigured) return;
    getReports()
      .then((docs) => {
        if (docs.length) {
          setReports(
            docs.map((d) => ({
              ...d,
              image: d.imageUrl || d.image,
              file: d.pdfUrl || d.file,
            }))
          );
        }
      })
      .catch(() => {/* keep fallback on error */});
  }, []);

  // Sort strictly by date (year, then quarter) — newest first.
  const sorted = [...reports].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.quarter.localeCompare(a.quarter);
  });

  const featured = sorted[0];
  const archive = sorted.slice(1);
  const years = [...new Set(archive.map((r) => r.year))];

  return (
    <div className="blog-modern-page">

      {/* ── Ultra-Premium Image Hero ── */}
      <section
        className="premium-blog-hero"
        style={{ backgroundImage: `url('/img/intro-bg.jpg')` }}
      >
        <div className="hero-gradient-overlay"></div>
        <div className="container">
          <motion.div className="hero-content-grid" initial="hidden" animate="visible" variants={fadeUp}>
            <div className="hero-eyebrow-col">
              <span className="copper-eyebrow">Management Reports</span>
              <div className="vertical-accent"></div>
            </div>
            <div className="hero-title-col">
              <h1 className="massive-blog-title">
                Quarterly <br />
                <span className="hero-italic">Intelligence.</span>
              </h1>
              <p className="hero-subtext">
                Every quarter, our leadership publishes a candid review of the market — macro forces, textile
                performance, and the strategy steering Softwood forward. The full archive lives here.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Featured: Latest Report ── */}
      <section className="featured-article-section">
        <div className="container">
          <MotionLink
            to={`/report/${featured.id}`}
            state={{ report: featured }}
            className="featured-report-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="featured-report-aside">
              <ReportMedia image={featured.image} quarter={featured.quarter} className="featured-report-img" />
              <div className="featured-aside-overlay"></div>
              <div className="featured-aside-content">
                <span className="latest-badge">Latest Report</span>
                <div className="featured-quarter">
                  <span className="fq-quarter">{featured.quarter}</span>
                  <span className="fq-year">{fyShort(featured.year)}</span>
                </div>
                <span className="featured-daterange">{featured.date}</span>
              </div>
            </div>
            <div className="featured-report-body">
              <span className="article-category">Management Report</span>
              <h2>{featured.title}</h2>
              <p>{featured.abstract}</p>
              <div className="featured-report-footer">
                <div className="report-authors">
                  <span className="authors-label">Prepared by</span>
                  <span className="authors-names">{featured.authors}</span>
                </div>
                <span className="read-report-link">
                  Read Report <span className="arrow">&rarr;</span>
                </span>
              </div>
            </div>
          </MotionLink>
        </div>
      </section>

      {/* ── Report Archive (grouped by year) ── */}
      <section className="reports-archive-section">
        <div className="container">
          <motion.div className="section-header" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>Report Archive</h2>
            <div className="copper-divider"></div>
          </motion.div>

          {years.map((year) => (
            <div className="archive-year-block" key={year}>
              <div className="archive-year-label">
                <span>{fyLabel(year)}</span>
                <div className="year-line"></div>
              </div>

              <motion.div
                className="reports-grid"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
              >
                {archive
                  .filter((r) => r.year === year)
                  .map((report) => (
                    <MotionLink
                      to={`/report/${report.id}`}
                      state={{ report }}
                      className="report-card"
                      key={report.id}
                      variants={fadeUp}
                    >
                      <div className="report-card-media">
                        <ReportMedia image={report.image} quarter={report.quarter} />
                        <div className="report-media-overlay"></div>
                        <span className="report-quarter-tag">{report.quarter} · FY {fyShort(report.year)}</span>
                      </div>
                      <div className="report-card-content">
                        <div className="report-card-top">
                          <span className="report-daterange">{report.date}</span>
                          <span className="report-pages">{report.pages} pages · PDF</span>
                        </div>
                        <h3>{report.title}</h3>
                        <p>{report.abstract}</p>
                        <div className="report-card-footer">
                          <span className="report-authors-names">{report.authors}</span>
                          <span className="read-report-link small">
                            Read <span className="arrow">&rarr;</span>
                          </span>
                        </div>
                      </div>
                    </MotionLink>
                  ))}
              </motion.div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default Blog;
